import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useSessionStorage } from '@dhl/use-storage'

type TrackingKey = {
  barcode: string
  postalCode?: string
}

const emptyTrackingKey: TrackingKey = {
  barcode: '',
  postalCode: undefined,
}

type TrackingKeyContextValue = readonly [TrackingKey, (trackingKey: TrackingKey) => void]

const TrackingKeyContext = createContext<TrackingKeyContextValue | undefined>(undefined)

/**
 * Single source of truth for the tracking key. All consumers share this one
 * session-storage-backed state instead of each maintaining their own copy,
 * which previously caused the copies to drift out of sync.
 */
export const TrackingKeyProvider: FC<PropsWithChildren> = ({ children }) => {
  const [sessionTrackingKey, setSessionTrackingKey] = useSessionStorage<TrackingKey>('trackingKey', emptyTrackingKey)

  const trackingKey = sessionTrackingKey ?? emptyTrackingKey

  const setTrackingKey = useCallback(
    (newTrackingKey: TrackingKey) => {
      // Compare against the single authoritative value (no stale per-instance
      // copies), so a corrected postal code is never skipped.
      if (newTrackingKey.barcode !== trackingKey.barcode || newTrackingKey.postalCode !== trackingKey.postalCode) {
        setSessionTrackingKey(newTrackingKey)
      }
    },
    [trackingKey.barcode, trackingKey.postalCode, setSessionTrackingKey]
  )

  const value = useMemo<TrackingKeyContextValue>(() => [trackingKey, setTrackingKey], [trackingKey, setTrackingKey])

  return <TrackingKeyContext.Provider value={value}>{children}</TrackingKeyContext.Provider>
}

export const useTrackingKey = (initialTrackingKey?: TrackingKey): TrackingKeyContextValue => {
  const context = useContext(TrackingKeyContext)

  if (!context) {
    throw new Error('useTrackingKey must be used within a TrackingKeyProvider')
  }

  const [trackingKey, setTrackingKey] = context
  const hasSeeded = useRef(false)

  // Preserve the previous behaviour where passing an initial tracking key seeds
  // it (e.g. when deep-linking straight to a track & trace page) as long as
  // nothing has been stored yet.
  useEffect(() => {
    if (!hasSeeded.current && initialTrackingKey && !trackingKey.barcode) {
      hasSeeded.current = true
      setTrackingKey(initialTrackingKey)
    }
  }, [initialTrackingKey, trackingKey.barcode, setTrackingKey])

  return context
}
